import type { ReactElement } from 'react'

import type { Editor } from '@tiptap/react'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { I18nShape } from '../../../../i18n/types.js'
import { ImageSize, ImageAlign } from '../types.js'

import { CaptionIcon } from './icons/caption.js'

interface BaseImageActionType {
  tooltip: (editor: Editor) => string
  isDisabled?: (editor: Editor) => boolean
  disabledTooltip?: string
}

interface BooleanImageAction extends BaseImageActionType {
  icon: (editor: Editor) => ReactElement | string
  isActive: (editor: Editor) => boolean
  action?: (editor: Editor) => void
}

interface MultiOptionImageAction extends BaseImageActionType {
  options: Array<{
    label: (editor: Editor) => string
    action: (editor: Editor) => void
    isActive: (editor: Editor) => boolean
  }>
}

type ImageActionType = BooleanImageAction | MultiOptionImageAction

export const isMultiOptionImageAction = (
  action: ImageActionType,
): action is MultiOptionImageAction => {
  return 'options' in action
}

const disabledAlignmentProps = ($t: I18nShape['$t']) => ({
  isDisabled: (editor: Editor) =>
    editor.isActive('async-image', { size: ImageSize.Full }),
  disabledTooltip: $t({
    defaultMessage: 'Unavailable for full-width images',
    id: 'FigureOpts.UnavailableOnFullWidth',
  }),
})

export const imageOptionsGenerator = ({
  $t,
}: I18nShape): Record<string, ImageActionType[]> => ({
  sizings: [
    {
      tooltip: () =>
        $t({
          defaultMessage: 'Adjust size of the image',
          id: 'FigureOpts.ImageSize',
        }),
      options: [
        {
          label: () =>
            $t({
              defaultMessage: 'Best fit',
              id: 'FigureOpts.BestFit.Label',
            }),
          action: editor =>
            editor.commands.setImageAttributes({
              size: ImageSize.BestFit,
            }),
          isActive: editor =>
            editor.isActive('async-image', { size: ImageSize.BestFit }),
        },
        {
          label: () =>
            $t({
              defaultMessage: 'Original size',
              id: 'FigureOpts.OriginalSize.Label',
            }),
          action: editor =>
            editor.commands.setImageAttributes({
              size: ImageSize.Original,
            }),
          isActive: editor =>
            editor.isActive('async-image', { size: ImageSize.Original }),
        },
        {
          label: () =>
            $t({
              defaultMessage: 'Half width',
              id: 'FigureOpts.HalfWidth.Label',
            }),
          action: editor =>
            editor.commands.setImageAttributes({
              size: ImageSize.Half,
            }),
          isActive: editor =>
            editor.isActive('async-image', { size: ImageSize.Half }),
        },
        {
          label: () =>
            $t({
              defaultMessage: 'Full width',
              id: 'FigureOpts.FullWidth.Label',
            }),
          action: editor =>
            editor.commands.setImageAttributes({
              size: ImageSize.Full,
              align: ImageAlign.Center,
            }),
          isActive: editor =>
            editor.isActive('async-image', { size: ImageSize.Full }),
        },
      ],
    },
  ],
  alignments: [
    {
      tooltip: () =>
        $t({ defaultMessage: 'Align left', id: 'FigureOpts.AlignLeft' }),
      action: editor =>
        editor.commands.setImageAttributes({
          align: ImageAlign.Left,
        }),
      icon: () => <SvgIcon name="image-align-left" />,
      isActive: editor =>
        editor.isActive('async-image', { align: ImageAlign.Left }),
      ...disabledAlignmentProps($t),
    },
    {
      tooltip: () =>
        $t({
          defaultMessage: 'Align center',
          id: 'FigureOpts.AlignCenter',
        }),
      action: editor =>
        editor.commands.setImageAttributes({
          align: ImageAlign.Center,
        }),
      icon: () => <SvgIcon name="image-align-center" />,
      isActive: editor =>
        editor.isActive('async-image', { align: ImageAlign.Center }),
      ...disabledAlignmentProps($t),
    },
    {
      tooltip: () =>
        $t({
          defaultMessage: 'Align right',
          id: 'FigureOpts.AlignRight',
        }),
      action: editor =>
        editor.commands.setImageAttributes({
          align: ImageAlign.Right,
        }),
      icon: () => <SvgIcon name="image-align-right" />,
      isActive: editor =>
        editor.isActive('async-image', { align: ImageAlign.Right }),
      ...disabledAlignmentProps($t),
    },
    {
      tooltip: () =>
        $t({
          defaultMessage: 'Align image to left and wrap text around it',
          id: 'FigureOpts.WrapLeft',
        }),
      action: editor =>
        editor.commands.setImageAttributes({
          align: ImageAlign.FloatLeft,
        }),
      icon: () => <SvgIcon name="image-float-left" />,
      isActive: editor =>
        editor.isActive('async-image', { align: ImageAlign.FloatLeft }),
      ...disabledAlignmentProps($t),
    },
    {
      tooltip: () =>
        $t({
          defaultMessage: 'Align image to right and wrap text around it',
          id: 'FigureOpts.WrapRight',
        }),
      action: editor =>
        editor.commands.setImageAttributes({
          align: ImageAlign.FloatRight,
        }),
      icon: () => <SvgIcon name="image-float-right" />,
      isActive: editor =>
        editor.isActive('async-image', { align: ImageAlign.FloatRight }),
      ...disabledAlignmentProps($t),
    },
  ],
  information: [
    {
      tooltip: editor => {
        const hasDescription = editor.isActive('async-image', {
          hasDescription: true,
        })
        if (hasDescription) {
          return $t({
            defaultMessage: 'Remove image description',
            id: 'FigureOpts.Alt.Remove',
          })
        }
        return $t({
          defaultMessage: 'Add image description',
          id: 'FigureOpts.Alt.Add',
        })
      },
      icon: () => <CaptionIcon />,
      action: editor =>
        editor.commands.setImageAttributes({
          hasDescription: !editor.isActive('async-image', {
            hasDescription: true,
          }),
        }),
      isActive: editor =>
        editor.isActive('async-image', { hasDescription: true }),
    },
  ],
  actions: [
    {
      tooltip: () => $t({ defaultMessage: 'Delete', id: 'Generics.Delete' }),
      icon: () => <SvgIcon name="trash" />,
      action: editor => {
        editor.commands.deleteSelection()
      },
      isActive: _ => false,
    },
  ],
})
